(function () {
  'use strict';

  const GUEST_STORAGE_KEY = 'rfr_guest_cart_snapshot';
  let isSyncing = false;
  let syncTimeout = null;

  function getConfig() {
    return window.SharedCartConfig || {};
  }

  // Helper: Fetch current active cart from Shopify AJAX API
  async function getActiveCart() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('[SharedCart] Error fetching active cart:', e);
      return null;
    }
  }

  // Serialize active cart items into compact "variantId:quantity,variantId:quantity" string
  function serializeCartItems(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return '';
    }
    return cart.items.map(item => `${item.variant_id || item.id}:${item.quantity}`).join(',');
  }

  // Parse sharedCartId string (compact "id:qty" or JSON array) into line items array
  function parseSharedCartIdToItems(sharedCartIdStr) {
    if (!sharedCartIdStr) return [];

    // Try parsing as JSON array
    try {
      const parsed = JSON.parse(sharedCartIdStr);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(item => ({
          id: item.id || item.variant_id,
          quantity: item.quantity || 1,
          properties: item.properties || {}
        }));
      }
    } catch (e) {}

    // Try parsing as compact string "variantId:qty,variantId:qty"
    if (typeof sharedCartIdStr === 'string' && sharedCartIdStr.includes(':')) {
      const pairs = sharedCartIdStr.split(',');
      const items = [];
      pairs.forEach(pair => {
        const parts = pair.split(':');
        const id = parts[0] ? parts[0].trim() : null;
        const qty = parts[1] ? parseInt(parts[1].trim(), 10) : 1;
        if (id && qty > 0) {
          items.push({ id: id, quantity: qty });
        }
      });
      if (items.length > 0) return items;
    }

    return [];
  }

  // Sync current cart payload to Customer Metafield via App Proxy
  async function syncCartIdToMetafield(cartData) {
    const config = getConfig();
    if (!config.customerId || isSyncing) {
      return;
    }

    const cartPayloadStr = typeof cartData === 'string' ? cartData : serializeCartItems(cartData);

    // Avoid redundant calls if payload hasn't changed
    if (config.lastSyncedCartId === cartPayloadStr) {
      return;
    }

    isSyncing = true;
    try {
      const payload = {
        shop: config.shop || 'r-for-rabbit.myshopify.com',
        customerId: String(config.customerId),
        sharedCartId: String(cartPayloadStr)
      };

      const proxyUrl = config.proxyUrl || '/apps/zoho-ticket/customer/sharedcartid';
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        config.sharedCartId = cartPayloadStr;
        config.lastSyncedCartId = cartPayloadStr;
        console.log('[SharedCart] Successfully synced cartId to customer metafield:', cartPayloadStr);
      } else {
        console.warn('[SharedCart] Proxy response status:', response.status);
      }
    } catch (err) {
      console.error('[SharedCart] Error syncing cartId:', err);
    } finally {
      isSyncing = false;
    }
  }

  // Check remote cart and update local session without reloading the page
  async function checkAndSyncRemoteCart() {
    const config = getConfig();
    if (!config.customerId || isSyncing) return;

    try {
      const proxyUrl = config.proxyUrl || '/apps/zoho-ticket/customer/sharedcartid';
      const checkUrl = `${proxyUrl}?shop=${encodeURIComponent(config.shop || '')}&customerId=${encodeURIComponent(config.customerId)}`;
      
      const res = await fetch(checkUrl, { method: 'GET' });
      if (!res.ok) return;

      const data = await res.json();
      const remoteSharedCartId = data.sharedCartId || data.shared_cart_id || data.value;

      if (remoteSharedCartId !== undefined && remoteSharedCartId !== config.lastSyncedCartId) {
        console.log('[SharedCart] Remote cart payload updated. Syncing locally...', remoteSharedCartId);
        
        const activeCart = await getActiveCart();
        const activePayload = serializeCartItems(activeCart);

        if (activePayload !== remoteSharedCartId) {
          const remoteItems = parseSharedCartIdToItems(remoteSharedCartId);
          await fetch('/cart/clear.js', { method: 'POST' });

          if (remoteItems.length > 0) {
            const addRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: remoteItems })
            });
            if (addRes.ok) {
              const updatedCart = await addRes.json();
              config.sharedCartId = remoteSharedCartId;
              config.lastSyncedCartId = remoteSharedCartId;
              document.dispatchEvent(new Event('cart:build'));
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: updatedCart }));
            }
          } else {
            config.sharedCartId = '';
            config.lastSyncedCartId = '';
            document.dispatchEvent(new Event('cart:build'));
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { items: [], item_count: 0 } }));
          }
        }
      }
    } catch (e) {
      // Gracefully ignore GET polling if proxy endpoint is POST-only
    }
  }

  // Save guest cart items to sessionStorage when guest user manages cart
  function snapshotGuestCart(cart) {
    const config = getConfig();
    if (!config.customerId && cart && Array.isArray(cart.items)) {
      if (cart.items.length > 0) {
        const items = cart.items.map(item => ({
          id: item.variant_id || item.id,
          quantity: item.quantity,
          properties: item.properties || {}
        }));
        sessionStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(items));
      } else {
        sessionStorage.removeItem(GUEST_STORAGE_KEY);
      }
    }
  }

  // Helper: Get item matching key
  function getItemKey(item) {
    const variantId = item.id || item.variant_id;
    const props = JSON.stringify(item.properties || {});
    return `${variantId}_${props}`;
  }

  // Merge items list smartly (prevents quantity doubling on sync)
  function mergeItems(guestItems, savedItems) {
    const itemMap = new Map();

    // 1. Add saved items first
    (savedItems || []).forEach(item => {
      const key = getItemKey(item);
      itemMap.set(key, {
        id: item.id || item.variant_id,
        quantity: item.quantity || 1,
        properties: item.properties || {}
      });
    });

    // 2. Merge pre-login guest items (use Math.max so duplicate items don't double in quantity)
    (guestItems || []).forEach(item => {
      const key = getItemKey(item);
      if (itemMap.has(key)) {
        const existing = itemMap.get(key);
        existing.quantity = Math.max(existing.quantity, item.quantity);
      } else {
        itemMap.set(key, {
          id: item.id || item.variant_id,
          quantity: item.quantity || 1,
          properties: item.properties || {}
        });
      }
    });

    return Array.from(itemMap.values());
  }

  // Main Cart Sync & Merge initialization
  async function initSharedCart() {
    const config = getConfig();
    const activeCart = await getActiveCart();
    if (!activeCart) return;

    // 1. Guest Mode Logic
    if (!config.customerId) {
      snapshotGuestCart(activeCart);
      return;
    }

    // 2. Customer Logged-in Mode Logic
    const rawGuestItems = sessionStorage.getItem(GUEST_STORAGE_KEY);
    let guestItems = [];
    if (rawGuestItems) {
      try {
        guestItems = JSON.parse(rawGuestItems);
      } catch (e) {
        guestItems = [];
      }
    }

    // Case A: Customer logged in and has pre-login guest items in sessionStorage to merge
    if (Array.isArray(guestItems) && guestItems.length > 0) {
      console.log('[SharedCart] Pre-login guest cart items detected. Merging cart...');
      
      const savedItems = parseSharedCartIdToItems(config.sharedCartId);
      const mergedList = mergeItems(guestItems, savedItems);

      sessionStorage.removeItem(GUEST_STORAGE_KEY);

      if (mergedList.length > 0) {
        await fetch('/cart/clear.js', { method: 'POST' });
        const updateRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: mergedList })
        });

        if (updateRes.ok) {
          const updatedCart = await updateRes.json();
          await syncCartIdToMetafield(updatedCart);
          document.dispatchEvent(new Event('cart:build'));
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: updatedCart }));
        }
      }
      return;
    }

    // Case B: Logged-in Customer on Device B / New Session -> align active cart to EXACT savedItems
    if (config.sharedCartId) {
      const savedItems = parseSharedCartIdToItems(config.sharedCartId);
      const activePayload = serializeCartItems(activeCart);

      // Only restore/align if local session cart differs from saved account cart
      if (activePayload !== config.sharedCartId) {
        console.log('[SharedCart] Aligning device cart with saved account cart...', savedItems);
        await fetch('/cart/clear.js', { method: 'POST' });

        if (savedItems.length > 0) {
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: savedItems })
          });

          if (addRes.ok) {
            const restoredCart = await addRes.json();
            config.lastSyncedCartId = config.sharedCartId;
            document.dispatchEvent(new Event('cart:build'));
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: restoredCart }));
          }
        } else {
          config.lastSyncedCartId = '';
          document.dispatchEvent(new Event('cart:build'));
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { items: [], item_count: 0 } }));
        }
      }
      return;
    }

    // Case C: Active session cart has items -> sync current cart to customer metafield
    if (activeCart.item_count > 0) {
      await syncCartIdToMetafield(activeCart);
    }
  }

  // Trigger debounced cart sync
  function triggerCartSyncDebounced() {
    clearTimeout(syncTimeout);
    syncTimeout = setTimeout(async () => {
      const cart = await getActiveCart();
      if (cart) {
        const config = getConfig();
        if (config.customerId) {
          syncCartIdToMetafield(cart);
        } else {
          snapshotGuestCart(cart);
        }
      }
    }, 350);
  }

  // Intercept network requests (both window.fetch AND XMLHttpRequest used by jQuery/theme drawers)
  function hookCartNetworkRequests() {
    // 1. Intercept window.fetch
    const originalFetch = window.fetch;
    if (originalFetch) {
      window.fetch = async function (input, init) {
        const response = await originalFetch.apply(this, arguments);
        let url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
        if (
          url &&
          (url.includes('/cart/add') ||
           url.includes('/cart/change') ||
           url.includes('/cart/update') ||
           url.includes('/cart/clear'))
        ) {
          triggerCartSyncDebounced();
        }
        return response;
      };
    }

    // 2. Intercept XMLHttpRequest (XHR) used by jQuery / Theme Cart Drawer
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._url = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      this.addEventListener('load', function () {
        if (
          this._url &&
          typeof this._url === 'string' &&
          (this._url.includes('/cart/add') ||
           this._url.includes('/cart/change') ||
           this._url.includes('/cart/update') ||
           this._url.includes('/cart/clear'))
        ) {
          triggerCartSyncDebounced();
        }
      });
      return originalSend.apply(this, arguments);
    };

    // 3. Tab Focus & Visibility Listeners for event-driven sync
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkAndSyncRemoteCart();
      }
    });

    window.addEventListener('focus', () => {
      checkAndSyncRemoteCart();
    });
  }

  // Initialize once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      hookCartNetworkRequests();
      initSharedCart();
    });
  } else {
    hookCartNetworkRequests();
    initSharedCart();
  }
})();
